import React from 'react';
import ReactDOM from 'react-dom';
import { X, Info, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * VraisemblanceMatrixModal - Modal displaying the vraisemblance calculation matrix
 * Shows the relationship between difficulty, probability, and vraisemblance
 */
const VraisemblanceMatrixModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const getVraisemblanceColor = (vraisemblance) => {
    switch (vraisemblance) {
      case 1: return 'bg-green-100 text-green-800 border-green-300';
      case 2: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 3: return 'bg-orange-100 text-orange-800 border-orange-300';
      case 4: return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getVraisemblanceLabel = (vraisemblance) => {
    return t(`common.likelihood.${vraisemblance === 1 ? 'low' : vraisemblance === 2 ? 'medium' : vraisemblance === 3 ? 'high' : 'critical'}`,
      vraisemblance === 1 ? 'Peu vraisemblable' : vraisemblance === 2 ? 'Vraisemblable' : vraisemblance === 3 ? 'Très vraisemblable' : 'Quasi certain'
    );
  };

  // Calculate vraisemblance based on probability and difficulty
  const calculateVraisemblance = (probability, difficulty) => {
    if (probability === 4) {
      if (difficulty === 4) return 2;
      else if (difficulty === 3) return 3;
      else if (difficulty === 2) return 4;
      else return 4;
    } else if (probability === 3) {
      if (difficulty === 4) return 2;
      else if (difficulty === 3) return 3;
      else if (difficulty === 2) return 3;
      else return 4;
    } else if (probability === 2) {
      if (difficulty === 4) return 1;
      else if (difficulty === 3) return 2;
      else if (difficulty === 2) return 3;
      else return 2;
    } else {
      if (difficulty === 4) return 1;
      else if (difficulty === 3) return 1;
      else if (difficulty === 2) return 2;
      else return 2;
    }
  };

  const getProbabilityLabel = (level) => {
    return t(`workshop4.operationalScenarios.vraisemblanceMatrix.probabilityLevels.${level}`);
  };

  const getDifficultyLabel = (level) => {
    return t(`workshop4.operationalScenarios.vraisemblanceMatrix.difficultyLevels.${level}`);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('workshop4.operationalScenarios.vraisemblanceMatrix.title', 'Likelihood Matrix')}</h2>
                <p className="text-sm opacity-90">{t('workshop4.operationalScenarios.vraisemblanceMatrix.subtitle', 'Calculation based on technical difficulty and success probability')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Explanation */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">{t('workshop4.operationalScenarios.vraisemblanceMatrix.howToUse', 'How to use this matrix')}</h3>
                <p className="text-sm text-blue-800 mb-2">
                  {t('workshop4.operationalScenarios.vraisemblanceMatrix.description', 'The likelihood of each attack phase is calculated by crossing:')}
                </p>
                <ul className="text-sm text-blue-800 space-y-1 ml-4">
                  <li>• <strong>{t('workshop4.operationalScenarios.vraisemblanceMatrix.technicalDifficulty', 'Technical Difficulty')}</strong> : {t('workshop4.operationalScenarios.vraisemblanceMatrix.technicalDifficultyDesc', 'Complexity of implementing the attack')}</li>
                  <li>• <strong>{t('workshop4.operationalScenarios.vraisemblanceMatrix.successProbability', 'Success Probability')}</strong> : {t('workshop4.operationalScenarios.vraisemblanceMatrix.successProbabilityDesc', 'Chance of success according to a cybersecurity expert')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Matrix Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 p-3 text-left font-semibold">
                    <div className="flex items-center space-x-2">
                      <span>{t('workshop4.operationalScenarios.vraisemblanceMatrix.successProbability', 'Success Probability')}</span>
                      <span className="text-xs text-gray-500">{t('workshop4.operationalScenarios.vraisemblanceMatrix.yAxis', '(Y-axis)')}</span>
                    </div>
                  </th>
                  <th className="border border-gray-300 p-3 text-center font-semibold bg-orange-50">
                    <div>{t('workshop4.operationalScenarios.vraisemblanceMatrix.difficulty', 'Difficulty')}: 4</div>
                    <div className="text-xs text-gray-600">{getDifficultyLabel(4)}</div>
                  </th>
                  <th className="border border-gray-300 p-3 text-center font-semibold bg-yellow-50">
                    <div>{t('workshop4.operationalScenarios.vraisemblanceMatrix.difficulty', 'Difficulty')}: 3</div>
                    <div className="text-xs text-gray-600">{getDifficultyLabel(3)}</div>
                  </th>
                  <th className="border border-gray-300 p-3 text-center font-semibold bg-green-50">
                    <div>{t('workshop4.operationalScenarios.vraisemblanceMatrix.difficulty', 'Difficulty')}: 2</div>
                    <div className="text-xs text-gray-600">{getDifficultyLabel(2)}</div>
                  </th>
                  <th className="border border-gray-300 p-3 text-center font-semibold bg-blue-50">
                    <div>{t('workshop4.operationalScenarios.vraisemblanceMatrix.difficulty', 'Difficulty')}: 1</div>
                    <div className="text-xs text-gray-600">{getDifficultyLabel(1)}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[4, 3, 2, 1].map(probability => (
                  <tr key={probability}>
                    <td className="border border-gray-300 p-3 font-semibold bg-gray-50">
                      <div>{t('workshop4.operationalScenarios.vraisemblanceMatrix.probability', 'Probability')}: {probability}</div>
                      <div className="text-xs text-gray-600">{getProbabilityLabel(probability)}</div>
                    </td>
                    {[4, 3, 2, 1].map(difficulty => {
                      const vraisemblance = calculateVraisemblance(probability, difficulty);
                      return (
                        <td key={difficulty} className="border border-gray-300 p-3 text-center">
                          <div className={`inline-block px-3 py-2 rounded-lg border font-semibold ${getVraisemblanceColor(vraisemblance)}`}>
                            <div className="text-lg">{vraisemblance}</div>
                            <div className="text-xs">{getVraisemblanceLabel(vraisemblance)}</div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`inline-block px-4 py-2 rounded-lg border font-semibold ${getVraisemblanceColor(1)}`}>
                1 - {getVraisemblanceLabel(1)}
              </div>
              <p className="text-xs text-gray-600 mt-1">{t('workshop4.operationalScenarios.vraisemblanceMatrix.riskLabels.1', 'Limited risk')}</p>
            </div>
            <div className="text-center">
              <div className={`inline-block px-4 py-2 rounded-lg border font-semibold ${getVraisemblanceColor(2)}`}>
                2 - {getVraisemblanceLabel(2)}
              </div>
              <p className="text-xs text-gray-600 mt-1">{t('workshop4.operationalScenarios.vraisemblanceMatrix.riskLabels.2', 'Moderate risk')}</p>
            </div>
            <div className="text-center">
              <div className={`inline-block px-4 py-2 rounded-lg border font-semibold ${getVraisemblanceColor(3)}`}>
                3 - {getVraisemblanceLabel(3)}
              </div>
              <p className="text-xs text-gray-600 mt-1">{t('workshop4.operationalScenarios.vraisemblanceMatrix.riskLabels.3', 'Significant risk')}</p>
            </div>
            <div className="text-center">
              <div className={`inline-block px-4 py-2 rounded-lg border font-semibold ${getVraisemblanceColor(4)}`}>
                4 - {getVraisemblanceLabel(4)}
              </div>
              <p className="text-xs text-gray-600 mt-1">{t('workshop4.operationalScenarios.vraisemblanceMatrix.riskLabels.4', 'Critical risk')}</p>
            </div>
          </div>

          {/* Example */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">{t('workshop4.operationalScenarios.vraisemblanceMatrix.example.title', 'Calculation example')}</h3>
            <p className="text-sm text-gray-700 mb-2">
              {t('workshop4.operationalScenarios.vraisemblanceMatrix.example.description', 'For a phase with Probability = 3 (High) and Difficulty = 2 (Easy):')}
            </p>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('workshop4.operationalScenarios.vraisemblanceMatrix.example.result', 'Likelihood =')}</span>
              <div className={`inline-block px-2 py-1 rounded border font-semibold ${getVraisemblanceColor(3)}`}>
                3 - {getVraisemblanceLabel(3)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VraisemblanceMatrixModal;